import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeScannerState } from 'html5-qrcode';
import { X, Camera as CameraIcon } from 'lucide-react';

/**
 * QRScanner modal: opens camera, scans a QR code and calls onScan(text).
 * Accepts both raw warranty numbers (e.g. "GDC-2026-0001")
 * and full URLs containing ?nr=GDC-2026-0001 query param.
 */
export default function QRScanner({ onScan, onClose }) {
  const containerId = 'qr-scanner-region';
  const scannerRef = useRef(null);
  const startedRef = useRef(false);
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const startScanner = async () => {
      // Defer construction until the DOM node exists.
      let scanner;
      try {
        scanner = new Html5Qrcode(containerId, { verbose: false });
        scannerRef.current = scanner;
      } catch (err) {
        if (!isMounted) return;
        setStarting(false);
        setError('Kunne ikke starte kamera: ' + (err && err.message ? err.message : String(err)));
        return;
      }

      try {
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decodedText) => {
            if (!isMounted) return;
            onScan(decodedText);
          },
          () => { /* ignore per-frame decode failures */ }
        );
        startedRef.current = true;
        if (isMounted) setStarting(false);
      } catch (err) {
        if (!isMounted) return;
        setStarting(false);
        const msg = (err && err.message) ? err.message : String(err);
        const lower = msg.toLowerCase();
        if (lower.includes('permission') || lower.includes('denied') || lower.includes('notallowed')) {
          setError('Kameratilgang ble avvist. Tillat kamera i nettleseren og prøv igjen.');
        } else if (lower.includes('notfound') || lower.includes('no camera') || lower.includes('devicesnotfound')) {
          setError('Fant ingen kamera på enheten.');
        } else {
          setError('Kunne ikke starte kamera: ' + msg);
        }
      }
    };

    startScanner();

    return () => {
      isMounted = false;
      const s = scannerRef.current;
      if (!s) return;
      // Only stop if scanner is actually scanning, otherwise stop() throws synchronously.
      try {
        const state = typeof s.getState === 'function' ? s.getState() : null;
        if (startedRef.current && state === Html5QrcodeScannerState.SCANNING) {
          s.stop().catch(() => {}).finally(() => {
            try { s.clear(); } catch { /* ignore */ }
          });
        } else {
          try { s.clear(); } catch { /* ignore */ }
        }
      } catch { /* swallow any cleanup error - never kill the parent */ }
      scannerRef.current = null;
      startedRef.current = false;
    };
  }, [onScan]);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="qr-scanner-modal">
      <div className="bg-white rounded-xl w-full max-w-md overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3 bg-emerald-900 text-white">
          <div className="flex items-center gap-2">
            <CameraIcon size={20} />
            <h3 className="font-semibold">Skann QR-kode</h3>
          </div>
          <button onClick={onClose} className="hover:bg-emerald-800 rounded-full p-1" data-testid="qr-scanner-close-btn" aria-label="Lukk">
            <X size={20} />
          </button>
        </div>

        <div className="p-4">
          {error ? (
            <div className="text-center py-8">
              <p className="text-red-600 mb-4" data-testid="qr-scanner-error">{error}</p>
              <button onClick={onClose} className="bg-stone-900 text-white px-6 py-2 rounded-lg hover:bg-stone-800">Lukk</button>
            </div>
          ) : (
            <>
              {starting && (
                <p className="text-center text-stone-500 mb-3">Starter kamera...</p>
              )}
              <div id={containerId} className="w-full rounded-lg overflow-hidden bg-black" style={{ minHeight: 280 }} />
              <p className="text-center text-sm text-stone-600 mt-3">
                Hold QR-koden fra kit-etiketten foran kameraet.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
